import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Button, Spinner, Input, Select } from '../../components/design-system';
import { ALMA_OPTION_LISTS, questionKeyFromTitle } from '../../lib/almaFlow/optionLists';
import type { AlmaFlowUsageRow, AlmaQuestionBranch, AlmaQuestionOption, HostSpecialty } from '../../types';

type EditingOption = {
  id: string;
  label: string;
  label_solo: string;
  label_partner: string;
  label_group: string;
};

type BranchDraft = {
  trigger_specialty_slug: string;
  title: string;
  helper: string;
  alma_line: string;
};

const EMPTY_BRANCH: BranchDraft = { trigger_specialty_slug: '', title: '', helper: '', alma_line: '' };

/** Empty optional fields fall back (to `label`, or no helper line), so store them as null. */
const orNull = (v: string) => (v.trim() ? v.trim() : null);

/** Trend of a pick count against the window before it. */
function trend(recent: number, previous: number): string {
  if (recent === 0 && previous === 0) return '–';
  if (previous === 0) return 'new';
  const pct = Math.round(((recent - previous) / previous) * 100);
  return pct === 0 ? 'flat' : `${pct > 0 ? '+' : ''}${pct}%`;
}

const USAGE_WINDOWS = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
];

const branchComplete = (d: BranchDraft) => !!(d.trigger_specialty_slug && d.title.trim() && d.alma_line.trim());

export function AdminAlmaQuestions() {
  const [options, setOptions] = useState<AlmaQuestionOption[]>([]);
  const [branches, setBranches] = useState<AlmaQuestionBranch[]>([]);
  const [specialties, setSpecialties] = useState<HostSpecialty[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingOption, setEditingOption] = useState<EditingOption | null>(null);
  const [editingBranch, setEditingBranch] = useState<{ id: string; draft: BranchDraft } | null>(null);
  const [newBranch, setNewBranch] = useState<BranchDraft | null>(null);
  const [newLabels, setNewLabels] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usage, setUsage] = useState<AlmaFlowUsageRow[]>([]);
  const [usageDays, setUsageDays] = useState('30');
  const [usageError, setUsageError] = useState(false);

  async function loadUsage(days: string) {
    const { data, error: rpcError } = await supabase.rpc('alma_flow_usage', { p_days: Number(days) });
    setUsageError(!!rpcError);
    setUsage(rpcError ? [] : ((data ?? []) as AlmaFlowUsageRow[]));
  }

  async function load() {
    const [opts, brs, specs] = await Promise.all([
      supabase.from('alma_question_options').select('*').order('sort_order'),
      supabase.from('alma_question_branches').select('*').order('created_at'),
      supabase.from('host_specialties').select('*').order('sort_order'),
    ]);
    setOptions((opts.data ?? []) as AlmaQuestionOption[]);
    setBranches((brs.data ?? []) as AlmaQuestionBranch[]);
    setSpecialties((specs.data ?? []) as HostSpecialty[]);
    setLoading(false);
  }

  useEffect(() => { void load(); }, []);
  useEffect(() => { void loadUsage(usageDays); }, [usageDays]);

  // ── Options ────────────────────────────────────────────────────────────────

  async function handleAddOption(questionKey: string) {
    const label = (newLabels[questionKey] ?? '').trim();
    if (!label) return;
    setSaving(true);
    const slug = label.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    const maxOrder = options
      .filter((o) => o.question_key === questionKey)
      .reduce((m, o) => Math.max(m, o.sort_order), -1);
    await supabase.from('alma_question_options').insert({ question_key: questionKey, label, slug, sort_order: maxOrder + 1 });
    setNewLabels((prev) => ({ ...prev, [questionKey]: '' }));
    setSaving(false);
    await load();
  }

  async function handleSaveOption() {
    if (!editingOption || !editingOption.label.trim()) return;
    setSaving(true);
    await supabase
      .from('alma_question_options')
      .update({
        label: editingOption.label.trim(),
        label_solo: orNull(editingOption.label_solo),
        label_partner: orNull(editingOption.label_partner),
        label_group: orNull(editingOption.label_group),
      })
      .eq('id', editingOption.id);
    setEditingOption(null);
    setSaving(false);
    await load();
  }

  async function handleReorder(list: AlmaQuestionOption[], id: string, direction: -1 | 1) {
    const idx = list.findIndex((o) => o.id === id);
    const swapIdx = idx + direction;
    if (swapIdx < 0 || swapIdx >= list.length) return;
    const a = list[idx];
    const b = list[swapIdx];
    await Promise.all([
      supabase.from('alma_question_options').update({ sort_order: b.sort_order }).eq('id', a.id),
      supabase.from('alma_question_options').update({ sort_order: a.sort_order }).eq('id', b.id),
    ]);
    await load();
  }

  async function toggleOption(item: AlmaQuestionOption) {
    setSaving(true);
    await supabase.from('alma_question_options').update({ active: !item.active }).eq('id', item.id);
    setSaving(false);
    await load();
  }

  // ── Branches ───────────────────────────────────────────────────────────────

  async function handleCreateBranch() {
    if (!newBranch || !branchComplete(newBranch)) return;
    setSaving(true);
    setError(null);
    const { error: insertError } = await supabase.from('alma_question_branches').insert({
      question_key: questionKeyFromTitle(newBranch.title, branches.map((b) => b.question_key)),
      trigger_specialty_slug: newBranch.trigger_specialty_slug,
      title: newBranch.title.trim(),
      helper: orNull(newBranch.helper),
      alma_line: newBranch.alma_line.trim(),
    });
    setSaving(false);
    if (insertError) {
      setError("Couldn't add the branch. Each specialty can only open one branch.");
      return;
    }
    setNewBranch(null);
    await load();
  }

  async function handleSaveBranch() {
    if (!editingBranch || !branchComplete(editingBranch.draft)) return;
    const { draft } = editingBranch;
    setSaving(true);
    setError(null);
    const { error: updateError } = await supabase
      .from('alma_question_branches')
      .update({
        trigger_specialty_slug: draft.trigger_specialty_slug,
        title: draft.title.trim(),
        helper: orNull(draft.helper),
        alma_line: draft.alma_line.trim(),
      })
      .eq('id', editingBranch.id);
    setSaving(false);
    if (updateError) {
      setError("Couldn't save the branch. Each specialty can only open one branch.");
      return;
    }
    setEditingBranch(null);
    await load();
  }

  async function toggleBranch(branch: AlmaQuestionBranch) {
    setSaving(true);
    await supabase.from('alma_question_branches').update({ active: !branch.active }).eq('id', branch.id);
    setSaving(false);
    await load();
  }

  if (loading) return <div className="flex justify-center py-16"><Spinner /></div>;

  const specialtyName = (slug: string) => specialties.find((s) => s.slug === slug)?.name ?? slug;

  /** Specialties a branch can trigger on: active ones not already used by another branch. */
  function triggerOptions(currentSlug: string) {
    const used = new Set(branches.map((b) => b.trigger_specialty_slug).filter((s) => s !== currentSlug));
    return specialties
      .filter((s) => s.active && s.slug !== 'other' && !used.has(s.slug))
      .map((s) => ({ value: s.slug, label: s.name }));
  }

  function renderBranchForm(draft: BranchDraft, onChange: (d: BranchDraft) => void, onSave: () => void, onCancel: () => void, saveLabel: string) {
    return (
      <div className="flex flex-col gap-3">
        <Select
          label="Shown when in the top three"
          value={draft.trigger_specialty_slug}
          onChange={(e) => onChange({ ...draft, trigger_specialty_slug: e.target.value })}
          options={triggerOptions(draft.trigger_specialty_slug)}
          placeholder="Pick a specialty"
        />
        <Input label="Title" value={draft.title} onChange={(e) => onChange({ ...draft, title: e.target.value })} placeholder="Where you'd like to eat" />
        <Input label="Helper line (optional)" value={draft.helper} onChange={(e) => onChange({ ...draft, helper: e.target.value })} placeholder="Pick all that fit." />
        <Input label="Alma's line before the question" value={draft.alma_line} onChange={(e) => onChange({ ...draft, alma_line: e.target.value })} placeholder="Food first, then." />
        <div className="flex items-center gap-3">
          <Button size="sm" onClick={onSave} disabled={saving || !branchComplete(draft)}>{saveLabel}</Button>
          <button onClick={onCancel} className="text-xs text-app-muted hover:text-app-fg">Cancel</button>
        </div>
      </div>
    );
  }

  function renderOptions(questionKey: string, perGroupLabels: boolean) {
    const list = options.filter((o) => o.question_key === questionKey);
    const active = list.filter((o) => o.active);
    const inactive = list.filter((o) => !o.active);
    const newLabel = newLabels[questionKey] ?? '';

    return (
      <>
        {/* Add new */}
        <div className="flex gap-3 mb-5">
          <div className="flex-1">
            <Input
              value={newLabel}
              onChange={(e) => setNewLabels((prev) => ({ ...prev, [questionKey]: e.target.value }))}
              placeholder="New option"
              onKeyDown={(e) => { if (e.key === 'Enter') void handleAddOption(questionKey); }}
            />
          </div>
          <Button onClick={() => void handleAddOption(questionKey)} disabled={saving || !newLabel.trim()}>Add</Button>
        </div>

        {/* Active */}
        <div className="flex flex-col gap-2">
          {active.map((item, idx) => (
            <div key={item.id} className="bg-app-card border border-app-border rounded-xl px-4 py-3">
              {editingOption?.id === item.id ? (
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-3">
                    <input
                      value={editingOption.label}
                      onChange={(e) => setEditingOption({ ...editingOption, label: e.target.value })}
                      aria-label="Default wording"
                      className="flex-1 bg-app-bg border border-app-border rounded-lg px-3 py-1.5 text-sm text-app-fg focus:outline-none"
                    />
                    <Button size="sm" onClick={() => void handleSaveOption()} disabled={saving || !editingOption.label.trim()}>Save</Button>
                    <button onClick={() => setEditingOption(null)} className="text-xs text-app-muted hover:text-app-fg">Cancel</button>
                  </div>
                  {perGroupLabels && (
                    <div className="grid gap-2 md:grid-cols-3">
                      {([
                        ['label_solo', 'Just me'],
                        ['label_partner', 'With a partner'],
                        ['label_group', 'Friends or family'],
                      ] as const).map(([field, groupName]) => (
                        <label key={field} className="flex flex-col gap-1">
                          <span className="text-xs text-app-muted">{groupName}</span>
                          <input
                            value={editingOption[field]}
                            onChange={(e) => setEditingOption({ ...editingOption, [field]: e.target.value })}
                            placeholder="Same as default"
                            className="bg-app-bg border border-app-border rounded-lg px-3 py-1.5 text-sm text-app-fg focus:outline-none"
                          />
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-app-fg">{item.label}</span>
                      <span className="text-xs text-app-muted">({item.slug})</span>
                    </div>
                    {perGroupLabels && (item.label_solo || item.label_partner || item.label_group) && (
                      <p className="text-xs text-app-muted mt-1">
                        {[
                          item.label_solo && `Just me: ${item.label_solo}`,
                          item.label_partner && `Partner: ${item.label_partner}`,
                          item.label_group && `Group: ${item.label_group}`,
                        ].filter(Boolean).join(' · ')}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => void handleReorder(active, item.id, -1)} disabled={idx === 0} className="p-1 text-app-muted hover:text-app-fg disabled:opacity-30 transition-colors" title="Move up">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                        <path fillRule="evenodd" d="M14.77 12.79a.75.75 0 01-1.06-.02L10 8.832 6.29 12.77a.75.75 0 11-1.08-1.04l4.25-4.5a.75.75 0 011.08 0l4.25 4.5a.75.75 0 01-.02 1.06z" clipRule="evenodd" />
                      </svg>
                    </button>
                    <button onClick={() => void handleReorder(active, item.id, 1)} disabled={idx === active.length - 1} className="p-1 text-app-muted hover:text-app-fg disabled:opacity-30 transition-colors" title="Move down">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                        <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                      </svg>
                    </button>
                    <button
                      onClick={() => setEditingOption({
                        id: item.id,
                        label: item.label,
                        label_solo: item.label_solo ?? '',
                        label_partner: item.label_partner ?? '',
                        label_group: item.label_group ?? '',
                      })}
                      className="text-xs text-app-muted hover:text-app-fg ml-2"
                    >
                      Edit
                    </button>
                    <button onClick={() => void toggleOption(item)} className="text-xs text-crimson/60 hover:text-crimson ml-2">Deactivate</button>
                  </div>
                </div>
              )}
            </div>
          ))}
          {active.length === 0 && (
            <p className="text-sm text-app-muted">No active options. Alma skips this question until at least one is added.</p>
          )}
        </div>

        {/* Inactive */}
        {inactive.length > 0 && (
          <div className="mt-6">
            <p className="text-xs uppercase tracking-widest text-app-muted font-medium mb-3">Deactivated</p>
            <div className="flex flex-col gap-2">
              {inactive.map((item) => (
                <div key={item.id} className="bg-app-card/50 border border-app-border rounded-xl px-4 py-3 flex items-center gap-3 opacity-60">
                  <span className="text-sm text-app-muted flex-1">{item.label} ({item.slug})</span>
                  <button onClick={() => void toggleOption(item)} className="text-xs text-success hover:text-success/80">Reactivate</button>
                </div>
              ))}
            </div>
          </div>
        )}
      </>
    );
  }

  function renderBranch(branch: AlmaQuestionBranch) {
    const isEditing = editingBranch?.id === branch.id;
    const trigger = specialties.find((s) => s.slug === branch.trigger_specialty_slug);
    return (
      <section key={branch.id} className={`mb-12 ${branch.active ? '' : 'opacity-60'}`}>
        {isEditing ? (
          <div className="mb-5">
            {renderBranchForm(
              editingBranch.draft,
              (draft) => setEditingBranch({ id: branch.id, draft }),
              () => void handleSaveBranch(),
              () => { setEditingBranch(null); setError(null); },
              'Save',
            )}
          </div>
        ) : (
          <div className="flex items-start gap-3 mb-5">
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-semibold text-app-fg">{branch.title}</h3>
              <p className="text-app-muted text-sm mt-1">
                Shown when <span className="text-app-fg">{specialtyName(branch.trigger_specialty_slug)}</span> is one of the top three priorities.
                {trigger && !trigger.active && ' That specialty is deactivated, so this branch is skipped.'}
              </p>
              <p className="text-app-muted text-sm mt-1">Alma: “{branch.alma_line}”{branch.helper ? ` · Helper: ${branch.helper}` : ''}</p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button
                onClick={() => setEditingBranch({
                  id: branch.id,
                  draft: {
                    trigger_specialty_slug: branch.trigger_specialty_slug,
                    title: branch.title,
                    helper: branch.helper ?? '',
                    alma_line: branch.alma_line,
                  },
                })}
                className="text-xs text-app-muted hover:text-app-fg"
              >
                Edit
              </button>
              {branch.active ? (
                <button onClick={() => void toggleBranch(branch)} className="text-xs text-crimson/60 hover:text-crimson ml-2">Deactivate</button>
              ) : (
                <button onClick={() => void toggleBranch(branch)} className="text-xs text-success hover:text-success/80 ml-2">Reactivate</button>
              )}
            </div>
          </div>
        )}
        {renderOptions(branch.question_key, false)}
      </section>
    );
  }

  /** Every option of a question (even unpicked ones, at 0) with its pick counts, most picked first. */
  function renderUsage(title: string, items: { slug: string; label: string }[], rows: AlmaFlowUsageRow[]) {
    const bySlug = new Map(rows.map((r) => [r.slug, r]));
    const known = new Set(items.map((i) => i.slug));
    const lines = [
      ...items.map((i) => ({ slug: i.slug, label: i.label, row: bySlug.get(i.slug) })),
      // Picks of options since renamed or deactivated, and the traveler's own "Other".
      ...rows.filter((r) => !known.has(r.slug)).map((r) => ({ slug: r.slug, label: r.label || r.slug, row: r })),
    ]
      .map((l) => ({ ...l, recent: l.row?.picks_recent ?? 0, previous: l.row?.picks_previous ?? 0, total: l.row?.picks_total ?? 0 }))
      .sort((a, b) => b.recent - a.recent || b.total - a.total);
    return (
      <div key={title} className="mb-8">
        <h3 className="text-sm font-semibold text-app-fg mb-2">{title}</h3>
        <div className="bg-app-card border border-app-border rounded-xl overflow-hidden">
          <div className="grid grid-cols-[1fr_64px_64px_64px_56px] gap-2 px-4 py-2 text-xs uppercase tracking-widest text-app-muted font-medium border-b border-app-border">
            <span>Option</span><span className="text-right">Now</span><span className="text-right">Before</span><span className="text-right">All time</span><span className="text-right">Trend</span>
          </div>
          {lines.map((l) => (
            <div key={l.slug} className="grid grid-cols-[1fr_64px_64px_64px_56px] gap-2 px-4 py-2 text-sm border-b border-app-border last:border-b-0">
              <span className="text-app-fg truncate">{l.label}</span>
              <span className="text-right tabular-nums text-app-fg">{l.recent}</span>
              <span className="text-right tabular-nums text-app-muted">{l.previous}</span>
              <span className="text-right tabular-nums text-app-muted">{l.total}</span>
              <span className="text-right tabular-nums text-app-muted">{trend(l.recent, l.previous)}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-app-bg text-app-fg">
      <div className="px-6 md:px-10 py-8 md:py-12">
        <h1 className="text-2xl md:text-3xl font-semibold text-app-fg mb-1">Alma Questions</h1>
        <p className="text-app-muted text-sm mb-10">
          Options for the quick questions Alma asks before the conversation. Cities come from Destinations and
          priorities from Host Specialties. Answers are saved by slug, so renaming an option is safe.
        </p>

        {/* Branch questions */}
        <h2 className="text-lg font-semibold text-app-fg mb-1">Branch questions</h2>
        <p className="text-app-muted text-sm mb-6">
          An extra question for each of the traveler's top three priorities that has one, asked in priority order.
          Skipped when it has no active options. Every follow-up also offers "Other", where the traveler types their own.
        </p>
        {error && <p className="text-sm text-crimson mb-4">{error}</p>}
        {branches.map(renderBranch)}
        <div className="mb-14">
          {newBranch ? (
            <div className="bg-app-card border border-app-border rounded-xl px-4 py-4">
              {renderBranchForm(
                newBranch,
                setNewBranch,
                () => void handleCreateBranch(),
                () => { setNewBranch(null); setError(null); },
                'Add branch',
              )}
            </div>
          ) : (
            <Button variant="secondary" onClick={() => setNewBranch(EMPTY_BRANCH)}>New branch</Button>
          )}
        </div>

        {/* Usage */}
        <h2 className="text-lg font-semibold text-app-fg mb-1">What travelers pick</h2>
        <p className="text-app-muted text-sm mb-4">
          How often each priority and option was picked in the finished chats, against the same-length window before it.
          Use it to reorder, rename or retire options. What travelers type under "Other" is never shown here.
        </p>
        <div className="max-w-[200px] mb-6">
          <Select label="Window" value={usageDays} onChange={(e) => setUsageDays(e.target.value)} options={USAGE_WINDOWS} />
        </div>
        {usageError && <p className="text-sm text-crimson mb-4">Couldn't load the usage numbers.</p>}
        {renderUsage(
          'Priorities',
          specialties.filter((sp) => sp.active).map((sp) => ({ slug: sp.slug, label: sp.name })),
          usage.filter((r) => r.kind === 'priority'),
        )}
        {branches.map((b) => renderUsage(
          b.title,
          options.filter((o) => o.question_key === b.question_key && o.active).map((o) => ({ slug: o.slug, label: o.label })),
          usage.filter((r) => r.kind === 'branch' && r.question_key === b.question_key),
        ))}
        <div className="mb-14" />

        {/* Fixed questions */}
        {ALMA_OPTION_LISTS.map((list) => (
          <section key={list.key} className="mb-12">
            <h2 className="text-lg font-semibold text-app-fg mb-1">{list.title}</h2>
            <p className="text-app-muted text-sm mb-5">{list.description}</p>
            {renderOptions(list.key, list.perGroupLabels)}
          </section>
        ))}
      </div>
    </div>
  );
}

export default AdminAlmaQuestions;
